import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { User, Session } from '@supabase/supabase-js';

export type UserPlan = 'light' | 'pro' | 'avancado';
export type TreatmentPreference = 'feminino' | 'masculino' | 'neutro' | 'nao-informar';

export interface UserProfile {
  id: string;
  name: string;
  email: string;
  phone: string;
  treatmentPreference: TreatmentPreference;
  city: string;
  state: string;
  plan: UserPlan;
  subscriptionStatus: 'ativa' | 'inativa' | 'teste';
  isAdmin: boolean;
  artisanRelation?: string;
  selfDescription?: string;
  mainGoal?: string;
  mainGoalOther?: string;
  currentTechniques?: string[];
  currentTechniquesOther?: string;
  manageTechniques?: string[];
  manageTechniquesOther?: string;
  futureTechniques?: string[];
  futureTechniquesOther?: string;
  logoUrl?: string;
  subscriptionEnd?: string;
  cancelAtPeriodEnd?: boolean;
}

export type MockUser = UserProfile;

const devMockUser: UserProfile = {
  id: 'dev-mock',
  name: 'Dev Preview',
  email: 'dev@preview.local',
  phone: '',
  treatmentPreference: 'feminino',
  city: '',
  state: '',
  plan: 'avancado',
  subscriptionStatus: 'ativa',
  isAdmin: true,
};

export function getGreeting(user: UserProfile | null): string {
  if (!user?.treatmentPreference) return 'Bem-vindo(a)';
  switch (user.treatmentPreference) {
    case 'feminino': return 'Bem-vinda';
    case 'masculino': return 'Bem-vindo';
    default: return 'Bem-vindo(a)';
  }
}

interface UserContextType {
  user: UserProfile | null;
  session: Session | null;
  isLoggedIn: boolean;
  loading: boolean;
  login: (email: string, password: string) => Promise<{ error?: string }>;
  signUp: (email: string, password: string, metadata: Record<string, string>, intendedPlan?: string) => Promise<{ error?: string; isExisting?: boolean; userId?: string }>;
  loginAsAdmin: () => void;
  logout: () => Promise<void>;
  setPlan: (plan: UserPlan) => void;
  updateUser: (data: Partial<UserProfile>) => Promise<void>;
  canAccess: (requiredPlan: UserPlan) => boolean;
  refreshProfile: () => Promise<void>;
}

const planHierarchy: UserPlan[] = ['light', 'pro', 'avancado'];

const UserContext = createContext<UserContextType>({} as UserContextType);

function mapProfileRow(row: any): Omit<UserProfile, 'isAdmin'> {
  return {
    id: row.id,
    name: row.name || '',
    email: row.email || '',
    phone: row.phone || '',
    treatmentPreference: (row.treatment_preference || 'feminino') as TreatmentPreference,
    city: row.city || '',
    state: row.state || '',
    plan: (row.plan || 'light') as UserPlan,
    subscriptionStatus: (row.subscription_status || 'ativa') as 'ativa' | 'inativa' | 'teste',
    artisanRelation: row.artisan_relation,
    selfDescription: row.self_description,
    mainGoal: row.main_goal,
    mainGoalOther: row.main_goal_other,
    currentTechniques: row.current_techniques,
    currentTechniquesOther: row.current_techniques_other,
    manageTechniques: row.manage_techniques,
    manageTechniquesOther: row.manage_techniques_other,
    futureTechniques: row.future_techniques,
    futureTechniquesOther: row.future_techniques_other,
    logoUrl: row.logo_url,
  };
}

export function UserProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<UserProfile | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const profileLoadingRef = useRef(false);
  const initializedRef = useRef(false);

  const loadProfile = useCallback(async (userId: string, retryCount = 0) => {
    if (profileLoadingRef.current && retryCount === 0) return;
    profileLoadingRef.current = true;

    try {
      const [{ data: profileRow }, { data: roleRows }, { data: subRow }] = await Promise.all([
        supabase.from('profiles').select('*').eq('id', userId).single(),
        supabase.from('user_roles').select('role').eq('user_id', userId),
        supabase.from('subscriptions').select('current_period_end, cancel_at_period_end').eq('user_id', userId).single(),
      ]);

      const isAdmin = roleRows?.some((r: any) => r.role === 'admin') ?? false;

      if (profileRow) {
        setUser({
          ...mapProfileRow(profileRow),
          isAdmin,
          subscriptionEnd: subRow?.current_period_end ?? undefined,
          cancelAtPeriodEnd: subRow?.cancel_at_period_end ?? undefined,
        });
      }
      profileLoadingRef.current = false;
      setLoading(false);
    } catch {
      profileLoadingRef.current = false;
      if (retryCount < 2) {
        setTimeout(() => loadProfile(userId, retryCount + 1), 2000);
      } else {
        setLoading(false);
      }
    }
  }, []);

  const refreshProfile = useCallback(async () => {
    if (session?.user) {
      profileLoadingRef.current = false; // Allow reload
      await loadProfile(session.user.id);
    }
  }, [session, loadProfile]);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, sess) => {
      setSession(sess);

      if (event === 'INITIAL_SESSION') {
        if (initializedRef.current) return;
        initializedRef.current = true;
      if (sess?.user) {
        loadProfile(sess.user.id);
      } else if (import.meta.env.DEV) {
        setUser(devMockUser);
        setLoading(false);
      } else {
        setLoading(false);
        }
        return;
      }

      if (sess?.user) {
        setTimeout(() => loadProfile(sess.user.id), 0);
      } else if (import.meta.env.DEV) {
        setUser(devMockUser);
        setLoading(false);
      } else {
        setUser(null);
        setLoading(false);
      }
    });

    supabase.auth.getSession().then(({ data: { session: sess } }) => {
      if (initializedRef.current) return;
      initializedRef.current = true;
      setSession(sess);
      if (sess?.user) {
        loadProfile(sess.user.id);
      } else if (import.meta.env.DEV) {
        setUser(devMockUser);
        setLoading(false);
      } else {
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, [loadProfile]);

  const login = useCallback(async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) return { error: error.message };
    return {};
  }, []);

  const signUp = useCallback(async (email: string, password: string, metadata: Record<string, string>, intendedPlan?: string): Promise<{ error?: string; isExisting?: boolean; userId?: string }> => {
    // Build dynamic redirect URL preserving plan intent
    let redirectUrl = 'https://artesa-app.com.br/auth/sucesso';
    if (intendedPlan && (intendedPlan === 'pro' || intendedPlan === 'avancado')) {
      redirectUrl += `?plano=${intendedPlan}`;
    }

    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: metadata,
        emailRedirectTo: redirectUrl,
      },
    });
    if (error) return { error: error.message };
    if (data.user && data.user.identities && data.user.identities.length === 0) {
      return { isExisting: true };
    }
    return { userId: data.user?.id };
  }, []);

  const loginAsAdmin = () => {};

  const logout = useCallback(async () => {
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
  }, []);

  const setPlan = useCallback((plan: UserPlan) => {
    if (!import.meta.env.DEV) return;
    setUser(prev => prev ? { ...prev, plan } : prev);
  }, []);

  const updateUser = useCallback(async (data: Partial<UserProfile>) => {
    setUser(prev => prev ? { ...prev, ...data } : prev);
    if (!session?.user) return;

    const dbData: Record<string, any> = {};
    if (data.name !== undefined) dbData.name = data.name;
    if (data.email !== undefined) dbData.email = data.email;
    if (data.phone !== undefined) dbData.phone = data.phone;
    if (data.treatmentPreference !== undefined) dbData.treatment_preference = data.treatmentPreference;
    if (data.city !== undefined) dbData.city = data.city;
    if (data.state !== undefined) dbData.state = data.state;
    if (data.plan !== undefined) dbData.plan = data.plan;
    if (data.artisanRelation !== undefined) dbData.artisan_relation = data.artisanRelation;
    if (data.selfDescription !== undefined) dbData.self_description = data.selfDescription;
    if (data.mainGoal !== undefined) dbData.main_goal = data.mainGoal;
    if (data.mainGoalOther !== undefined) dbData.main_goal_other = data.mainGoalOther;
    if (data.currentTechniques !== undefined) dbData.current_techniques = data.currentTechniques;
    if (data.currentTechniquesOther !== undefined) dbData.current_techniques_other = data.currentTechniquesOther;
    if (data.manageTechniques !== undefined) dbData.manage_techniques = data.manageTechniques;
    if (data.manageTechniquesOther !== undefined) dbData.manage_techniques_other = data.manageTechniquesOther;
    if (data.futureTechniques !== undefined) dbData.future_techniques = data.futureTechniques;
    if (data.futureTechniquesOther !== undefined) dbData.future_techniques_other = data.futureTechniquesOther;
    if (data.logoUrl !== undefined) dbData.logo_url = data.logoUrl;

    if (Object.keys(dbData).length > 0) {
      dbData.updated_at = new Date().toISOString();
      await supabase.from('profiles').update(dbData as any).eq('id', session.user.id);
    }
  }, [session]);

  const canAccess = useCallback((requiredPlan: UserPlan): boolean => {
    if (!user) return false;
    return planHierarchy.indexOf(user.plan) >= planHierarchy.indexOf(requiredPlan);
  }, [user]);

  return (
    <UserContext.Provider value={{ user, session, isLoggedIn: !!session?.user || (import.meta.env.DEV && user?.id === 'dev-mock'), loading, login, signUp, loginAsAdmin, logout, setPlan, updateUser, canAccess, refreshProfile }}>
      {children}
    </UserContext.Provider>
  );
}

export const useUser = () => useContext(UserContext);
